import { logger } from "@/utils/logger";
// 通知服务

import { apiService } from "./api";
import { httpApi } from "@/utils/http";
import type { PageResponse } from "@/types/api";
import { ElNotification, ElMessage, ElMessageBox } from "element-plus";

/**
 * 通知类型枚举
 */
export enum NotificationType {
  SUCCESS = "success",
  WARNING = "warning",
  INFO = "info",
  ERROR = "error",
}

/**
 * 通知消息接口
 */
export interface NotificationMessage {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: number;
  duration?: number;
  showClose?: boolean;
  data?: Record<string, any>;
}

/**
 * 系统通知接口
 */
export interface SystemNotification {
  id: string;
  userId?: string;
  type: "SYSTEM" | "TRADING" | "RISK" | "ACCOUNT" | "MARKET";
  level: "INFO" | "WARNING" | "ERROR" | "CRITICAL";
  title: string;
  content: string;
  category: string;
  status: "UNREAD" | "READ" | "ARCHIVED";
  persistent: boolean;
  actionRequired: boolean;
  actions?: Array<{
    id: string;
    label: string;
    type: "button" | "link";
    url?: string;
    callback?: string;
  }>;
  metadata?: Record<string, any>;
  createTime: string;
  readTime?: string;
  expireTime?: string;
}

/**
 * 推送订阅接口
 */
export interface PushSubscription {
  userId: string;
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  userAgent: string;
  createTime: string;
  lastUsed?: string;
  status: "ACTIVE" | "INACTIVE";
}

/**
 * 通知设置接口
 */
export interface NotificationSettings {
  userId: string;
  channels: {
    inApp: boolean;
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  categories: {
    system: boolean;
    trading: boolean;
    risk: boolean;
    account: boolean;
    market: boolean;
  };
  filters: {
    minLevel: "INFO" | "WARNING" | "ERROR" | "CRITICAL";
    workingHoursOnly: boolean;
    allowDuplicates: boolean;
    maxPerHour: number;
  };
  schedule: {
    enabled: boolean;
    quietHours: {
      start: string; // HH:mm
      end: string; // HH:mm
    };
    weekends: boolean;
    holidays: boolean;
  };
}

/**
 * 通知模板接口
 */
export interface NotificationTemplate {
  id: string;
  name: string;
  type: string;
  category: string;
  subject: string;
  content: string;
  variables: string[];
  channels: string[];
  status: "ACTIVE" | "INACTIVE";
  createTime: string;
  updateTime: string;
}

/**
 * 通知统计接口
 */
export interface NotificationStats {
  period: string;
  total: number;
  byType: Record<string, number>;
  byLevel: Record<string, number>;
  byChannel: Record<string, number>;
  readRate: number;
  responseTime: number;
  errorRate: number;
}

/**
 * 通知服务类
 */
class NotificationService {
  private readonly maxInAppNotifications = 100;
  private inAppNotifications: NotificationMessage[] = [];
  private subscriptions: Map<string, Set<(notification: NotificationMessage) => void>> = new Map();

  /**
   * 显示应用内通知
   */
  showNotification(options: {
    type: NotificationType;
    title: string;
    message: string;
    duration?: number;
    showClose?: boolean;
    onClick?: () => void;
    onClose?: () => void;
  }): string {
    const notification = ElNotification({
      type: options.type,
      title: options.title,
      message: options.message,
      duration: options.duration || 4500,
      showClose: options.showClose !== false,
      onClick: options.onClick,
      onClose: options.onClose,
    });

    const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const notificationMessage: NotificationMessage = {
      id,
      type: options.type,
      title: options.title,
      message: options.message,
      timestamp: Date.now(),
      duration: options.duration,
      showClose: options.showClose,
    };

    this.addToInAppList(notificationMessage);
    this.notifySubscribers("notification", notificationMessage);

    return id;
  }

  /**
   * 显示成功消息
   */
  success(title: string, message?: string, duration?: number): string {
    return this.showNotification({
      type: NotificationType.SUCCESS,
      title,
      message: message || "",
      duration,
    });
  }

  /**
   * 显示警告消息
   */
  warning(title: string, message?: string, duration?: number): string {
    return this.showNotification({
      type: NotificationType.WARNING,
      title,
      message: message || "",
      duration,
    });
  }

  /**
   * 显示信息消息
   */
  info(title: string, message?: string, duration?: number): string {
    return this.showNotification({
      type: NotificationType.INFO,
      title,
      message: message || "",
      duration,
    });
  }

  /**
   * 显示错误消息
   */
  error(title: string, message?: string, duration?: number): string {
    return this.showNotification({
      type: NotificationType.ERROR,
      title,
      message: message || "",
      duration: duration || 0, // 错误消息默认不自动关闭
    });
  }

  /**
   * 显示简单消息提示
   */
  message(
    message: string,
    type: NotificationType = NotificationType.INFO,
    duration?: number,
  ): void {
    ElMessage({
      message,
      type,
      duration: duration || 3000,
      showClose: true,
    });
  }

  /**
   * 显示确认对话框
   */
  async confirm(
    message: string,
    title: string = "确认",
    options?: {
      confirmButtonText?: string;
      cancelButtonText?: string;
      type?: "warning" | "info" | "success" | "error";
    },
  ): Promise<boolean> {
    try {
      await ElMessageBox.confirm(message, title, {
        confirmButtonText: options?.confirmButtonText || "确定",
        cancelButtonText: options?.cancelButtonText || "取消",
        type: options?.type || "warning",
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 显示提示对话框
   */
  async alert(
    message: string,
    title: string = "提示",
    type: "warning" | "info" | "success" | "error" = "info",
  ): Promise<void> {
    await ElMessageBox.alert(message, title, {
      confirmButtonText: "确定",
      type,
    });
  }

  /**
   * 显示输入对话框
   */
  async prompt(
    message: string,
    title: string = "输入",
    options?: {
      inputValue?: string;
      inputType?: string;
      inputValidator?: (value: string) => boolean | string;
      inputErrorMessage?: string;
    },
  ): Promise<string | null> {
    try {
      const { value } = await ElMessageBox.prompt(message, title, {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        inputValue: options?.inputValue,
        inputType: options?.inputType,
        inputValidator: options?.inputValidator,
        inputErrorMessage: options?.inputErrorMessage,
      });
      return value;
    } catch {
      return null;
    }
  }

  /**
   * 获取应用内通知列表
   */
  getInAppNotifications(): NotificationMessage[] {
    return [...this.inAppNotifications];
  }

  /**
   * 清除所有应用内通知
   */
  clearInAppNotifications(): void {
    this.inAppNotifications = [];
    this.notifySubscribers("clear", null);
  }

  /**
   * 订阅通知事件
   */
  subscribe(
    event: string,
    callback: (notification: NotificationMessage | null) => void,
  ): () => void {
    if (!this.subscriptions.has(event)) {
      this.subscriptions.set(event, new Set());
    }

    this.subscriptions.get(event)!.add(callback);

    return () => {
      this.subscriptions.get(event)?.delete(callback);
    };
  }

  /**
   * 获取系统通知
   */
  async getSystemNotifications(params: {
    type?: string;
    level?: string;
    status?: "UNREAD" | "READ" | "ARCHIVED";
    category?: string;
    startTime?: number;
    endTime?: number;
    page?: number;
    pageSize?: number;
  }): Promise<PageResponse<SystemNotification>> {
    return apiService.getPage<SystemNotification>("/api/v1/notifications", params);
  }

  /**
   * 标记通知为已读
   */
  async markAsRead(notificationId: string): Promise<void> {
    await apiService.post(`/api/v1/notifications/${notificationId}/read`);
  }

  /**
   * 批量标记通知为已读
   */
  async markAllAsRead(params?: { type?: string; category?: string }): Promise<void> {
    await apiService.post("/api/v1/notifications/read-all", params);
  }

  /**
   * 归档通知
   */
  async archiveNotification(notificationId: string): Promise<void> {
    await apiService.post(`/api/v1/notifications/${notificationId}/archive`);
  }

  /**
   * 删除通知
   */
  async deleteNotification(notificationId: string): Promise<void> {
    await apiService.delete(`/api/v1/notifications/${notificationId}`);
  }

  /**
   * 获取未读通知数量
   */
  async getUnreadCount(): Promise<{
    total: number;
    byType: Record<string, number>;
    byLevel: Record<string, number>;
  }> {
    return apiService.get("/api/v1/notifications/unread-count");
  }

  /**
   * 发送通知
   */
  async sendNotification(params: {
    userId?: string;
    userIds?: string[];
    type: string;
    level: "INFO" | "WARNING" | "ERROR" | "CRITICAL";
    title: string;
    content: string;
    category: string;
    channels?: string[];
    persistent?: boolean;
    actionRequired?: boolean;
    actions?: Array<{
      id: string;
      label: string;
      type: "button" | "link";
      url?: string;
      callback?: string;
    }>;
    metadata?: Record<string, any>;
    templateId?: string;
    templateData?: Record<string, any>;
    scheduleTime?: number;
    expireTime?: number;
  }): Promise<{
    notificationId: string;
    recipients: number;
    channels: string[];
  }> {
    return apiService.post("/api/v1/notifications/send", params);
  }

  /**
   * 获取通知设置
   */
  async getNotificationSettings(): Promise<NotificationSettings> {
    return await httpApi.get<NotificationSettings>("/api/v1/notifications/settings");
  }

  /**
   * 更新通知设置
   */
  async updateNotificationSettings(
    settings: Partial<NotificationSettings>,
  ): Promise<NotificationSettings> {
    return await httpApi.put<NotificationSettings>("/api/v1/notifications/settings", settings);
  }

  /**
   * 注册推送订阅
   */
  async registerPushSubscription(subscription: {
    endpoint: string;
    keys: {
      p256dh: string;
      auth: string;
    };
  }): Promise<void> {
    await apiService.post("/api/v1/notifications/push/subscribe", subscription);
  }

  /**
   * 取消推送订阅
   */
  async unregisterPushSubscription(endpoint: string): Promise<void> {
    await apiService.post("/api/v1/notifications/push/unsubscribe", { endpoint });
  }

  /**
   * 测试推送通知
   */
  async testPushNotification(): Promise<void> {
    await apiService.post("/api/v1/notifications/push/test");
  }

  /**
   * 获取通知模板
   */
  async getNotificationTemplates(): Promise<NotificationTemplate[]> {
    return apiService.get<NotificationTemplate[]>("/api/v1/notifications/templates");
  }

  /**
   * 创建通知模板
   */
  async createNotificationTemplate(
    template: Omit<NotificationTemplate, "id" | "createTime" | "updateTime">,
  ): Promise<NotificationTemplate> {
    return apiService.post<NotificationTemplate>("/api/v1/notifications/templates", template);
  }

  /**
   * 更新通知模板
   */
  async updateNotificationTemplate(
    id: string,
    updates: Partial<NotificationTemplate>,
  ): Promise<NotificationTemplate> {
    return apiService.put<NotificationTemplate>(`/api/v1/notifications/templates/${id}`, updates);
  }

  /**
   * 删除通知模板
   */
  async deleteNotificationTemplate(id: string): Promise<void> {
    await apiService.delete(`/api/v1/notifications/templates/${id}`);
  }

  /**
   * 获取通知统计
   */
  async getNotificationStats(params: {
    period: "hour" | "day" | "week" | "month";
    startTime?: number;
    endTime?: number;
  }): Promise<NotificationStats> {
    return apiService.get<NotificationStats>("/api/v1/notifications/stats", { params });
  }

  /**
   * 添加到应用内通知列表
   */
  private addToInAppList(notification: NotificationMessage): void {
    this.inAppNotifications.unshift(notification);

    // 限制最大数量
    if (this.inAppNotifications.length > this.maxInAppNotifications) {
      this.inAppNotifications = this.inAppNotifications.slice(0, this.maxInAppNotifications);
    }
  }

  /**
   * 通知订阅者
   */
  private notifySubscribers(event: string, notification: NotificationMessage | null): void {
    const callbacks = this.subscriptions.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(notification);
        } catch (error) {
          logger.error("通知回调执行失败:", error);
        }
      });
    }
  }
}

// 创建通知服务实例
export const notificationService = new NotificationService();

// 便捷方法导出
export const {
  success: notifySuccess,
  warning: notifyWarning,
  info: notifyInfo,
  error: notifyError,
  message: showMessage,
  confirm: confirmDialog,
  alert: alertDialog,
  prompt: promptDialog,
} = notificationService;

export default notificationService;
